import { Dimmer, Placeholder, Segment, Table } from '@mairistem/composants';
import { Sens } from '@mairistem/comptabilite';
import {
	formatMontant,
	getErrorMessage,
	type MetaRequest,
} from '@mairistem/finances-utils';
import Decimal from 'decimal.js';
import React from 'react';
import { toast } from 'react-toastify';
import type Composant from '../../interfaces/common/composant';
import type Financement from '../../interfaces/common/financement';
import type Projet from '../../interfaces/common/projet';
import type Filters from '../../interfaces/pluriannuelle/filters';
import type TotauxPluriannuelle from '../../interfaces/pluriannuelle/totauxPluriannuelle';
import { getProjets, getTotauxPluriannuelle } from '../../services/projet';
import TreeViewLibelleRow from '../common/treeView/TreeViewLibelleRow';
import PluriannuelleFilters, {
	DEFAULT_OPTION_FILTERS,
	OPTION_ANNEE_LIBRE,
} from './PluriannuelleFilters';
import PluriannuelleFooter from './PluriannuelleFooter';
import PluriannuelleHeader from './PluriannuelleHeader';
import './Pluriannuelle.css';

const DEFAULT_FILTERS = {
	endYear: DEFAULT_OPTION_FILTERS.endYear,
	periode: DEFAULT_OPTION_FILTERS.value,
	startYear: DEFAULT_OPTION_FILTERS.startYear,
};

const Pluriannuelle = () => {
	const [loading, setLoading] = React.useState(true);
	const [loadingTotaux, setLoadingTotaux] = React.useState(true);

	const [data, setData] = React.useState<Projet[]>([]);
	const [metadata, setMetadata] = React.useState<MetaRequest>(null);
	const [filters, setFilters] = React.useState<Filters>(DEFAULT_FILTERS);
	const [totaux, setTotaux] = React.useState<TotauxPluriannuelle>(null);
	const containerRef = React.useRef<HTMLDivElement>(null);

	const loadData = async (
		newFilters: Filters,
		page = 1,
		shouldReset = false,
	) => {
		try {
			setLoading(true);
			const result = await getProjets(
				new URLSearchParams({
					withComposants: 'true',
					withFinancements: 'true',
					withEtiquette: 'true',
					sens: Sens.DEPENSE,
					anneeDebut: newFilters.startYear,
					anneeFin: newFilters.endYear,
					page: page.toString(),
					perPage: '15',
				}),
			);

			if (shouldReset) {
				setData(result.data);

				if (containerRef.current) {
					containerRef.current.scrollTop = 0;
				}
			} else {
				setData((old) => [...old, ...result.data]);
			}
			setMetadata(result.meta);
		} catch (error) {
			toast.error(
				getErrorMessage(error, 'Erreur lors du chargement des projets'),
			);
		} finally {
			setLoading(false);
		}
	};

	const loadTotaux = async (newFilters: Filters) => {
		try {
			setLoadingTotaux(true);
			const result = await getTotauxPluriannuelle(
				new URLSearchParams({
					anneeDebut: newFilters.startYear,
					anneeFin: newFilters.endYear,
					withEtiquette: 'true',
					sens: Sens.DEPENSE,
				}),
			);
			setTotaux(result);
		} catch (error) {
			toast.error(
				getErrorMessage(
					error,
					'Erreur lors du chargement des totaux du projets',
				),
			);
		} finally {
			setLoadingTotaux(false);
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: Pas besoin de 'loadData' et 'loadTotaux'
	React.useEffect(() => {
		loadData(filters);
		loadTotaux(filters);
	}, []);

	const handleChangeYear = ({
		target: { name, value },
	}: React.FocusEvent<HTMLInputElement>) => {
		setFilters((old) => {
			const newFilters = { ...old, [name]: value };

			if (newFilters.startYear && newFilters.endYear) {
				if (
					newFilters.startYear.length !== 4 ||
					newFilters.endYear.length !== 4
				) {
					toast.error('Les années sont incorrectes');
					return old;
				}
				const startYear = Number(newFilters.startYear);
				const endYear = Number(newFilters.endYear);

				if (startYear > endYear) {
					toast.error("L'année de début est supérieure à celle de fin");
					return old;
				}
				if (endYear - startYear > 14) {
					toast.error(
						"Il y'a un écart de plus de 15 ans entre les deux années",
					);
					return old;
				}
				loadData(newFilters, 1, true);
				loadTotaux(newFilters);
			}

			return newFilters;
		});
	};

	const handleChangePeriode = (
		_e: React.ChangeEvent<HTMLSelectElement>,
		dropdown,
	) => {
		const selected = dropdown.options.find(
			(opt: any) => opt.value === dropdown.value,
		);
		setFilters((old) => {
			const newFilters = {
				...old,
				periode: dropdown.value,
				endYear: selected?.endYear,
				startYear: selected?.startYear,
			};

			if (dropdown.value !== OPTION_ANNEE_LIBRE) {
				loadData(newFilters, 1, true);
				loadTotaux(newFilters);
			}

			return {
				...old,
				periode: dropdown.value,
				endYear: selected?.endYear,
				startYear: selected?.startYear,
			};
		});
	};

	const years = Array.from(
		{ length: Number(filters.endYear) - Number(filters.startYear) + 1 },
		(_, i) => Number(filters.startYear) + i,
	);

	const getFinancementByYear = (year: number, financements: Financement[]) =>
		financements.find((p) => p.exercice === year);

	const renderFinancementRow = (
		identifiantProjet: string | number,
		financements: Financement[],
	) => (
		<>
			{years.map((year) => (
				<Table.Cell key={`${identifiantProjet}-${year}`} textAlign="right">
					{formatMontant(
						getFinancementByYear(year, financements)?.montant || 0,
					)}
				</Table.Cell>
			))}
		</>
	);

	const renderComposantRow = (
		identifiantProjet: string | number,
		composants: Composant[],
	) => {
		const result = [];
		years.forEach((year) => {
			const montant = composants?.reduce((acc, composant) => {
				let mnt = acc;
				composant.financements.forEach((financement) => {
					if (
						financement.exercice === year &&
						financement.sens === Sens.DEPENSE
					) {
						mnt = mnt
							.add(financement.montant)
							.add(financement.revision)
							.add(financement.rarN1)
							.sub(financement.rarN);
					}
				});

				return mnt;
			}, new Decimal(0));

			result.push(
				<Table.Cell key={`${identifiantProjet}-${year}`} textAlign="right">
					{formatMontant(montant.toNumber() || 0)}
				</Table.Cell>,
			);
		});

		return result;
	};

	const getAnneeTotal = (year: number) => {
		const result = totaux?.financementsExercice?.find(
			(financement) => financement.exercice === year,
		);
		return formatMontant(result?.montant ?? 0);
	};

	const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
		if (loading) return;

		const el = e.currentTarget;
		if (
			el.scrollTop + el.clientHeight >= el.scrollHeight - 50 &&
			metadata.currentPage + 1 < metadata.lastPage
		) {
			loadData(filters, metadata.currentPage + 1);
			setMetadata((old) => ({
				...old,
				currentPage: old.currentPage + 1,
			}));
		}
	};

	return (
		<Segment basic>
			<Segment basic>
				<PluriannuelleHeader
					endYear={filters.endYear}
					startYear={filters.startYear}
				/>
			</Segment>
			<Segment>
				<PluriannuelleFilters
					filters={filters}
					onChangeYear={handleChangeYear}
					onChangePeriode={handleChangePeriode}
				/>
			</Segment>
			<Segment>
				<div
					ref={containerRef}
					onScroll={handleScroll}
					className="pluriannuelle-table-container"
				>
					<Table celled>
						<Table.Header>
							<Table.Row>
								<Table.HeaderCell>Autorisation / Idée</Table.HeaderCell>
								<Table.HeaderCell>Politique</Table.HeaderCell>
								<Table.HeaderCell textAlign="right">
									Montant total
								</Table.HeaderCell>
								{years.map((year) => (
									<Table.HeaderCell key={year} textAlign="right">
										{year}
									</Table.HeaderCell>
								))}
							</Table.Row>
						</Table.Header>
						<Table.Body>
							{data.length === 0 && !loading && (
								<Table.Row>
									<Table.Cell colSpan={4 + years.length} textAlign="center">
										Aucun projet trouvé pour les critères sélectionnés.
									</Table.Cell>
								</Table.Row>
							)}
							{data.map((projet) => (
								<Table.Row key={projet.identifiant}>
									<Table.Cell className="row-libelle-cell">
										<TreeViewLibelleRow
											code={projet.code}
											decision={projet.decision}
											libelle={projet.libelle}
										/>
									</Table.Cell>
									<Table.Cell>{projet?.etiquette?.libelle}</Table.Cell>
									<Table.Cell textAlign="right">
										{formatMontant(projet?.montantAutorisation)}
									</Table.Cell>

									{projet.financements.length > 0
										? renderFinancementRow(
												projet.identifiant,
												projet.financements,
											)
										: renderComposantRow(projet.identifiant, projet.composants)}
								</Table.Row>
							))}
						</Table.Body>

						<Table.Footer>
							{loadingTotaux ? (
								<Table.Row>
									<Table.HeaderCell colSpan={4 + years.length}>
										Chargement des totaux...
									</Table.HeaderCell>
								</Table.Row>
							) : (
								<Table.Row className="pluriannuelleFooterRow">
									<Table.HeaderCell>Totaux</Table.HeaderCell>
									<Table.HeaderCell />
									<Table.HeaderCell />
									{years.map((year) => (
										<Table.HeaderCell key={`totaux-${year}`} textAlign="right">
											{getAnneeTotal(year)}
										</Table.HeaderCell>
									))}
								</Table.Row>
							)}
						</Table.Footer>
					</Table>

					<Dimmer active={loading} inverted>
						<Placeholder>
							<Placeholder.Header image>
								<Placeholder.Line />
								<Placeholder.Line />
							</Placeholder.Header>
							<Placeholder.Paragraph>
								<Placeholder.Line />
								<Placeholder.Line />
								<Placeholder.Line />
								<Placeholder.Line />
							</Placeholder.Paragraph>

							<Placeholder.Header image>
								<Placeholder.Line />
								<Placeholder.Line />
							</Placeholder.Header>
							<Placeholder.Paragraph>
								<Placeholder.Line />
								<Placeholder.Line />
								<Placeholder.Line />
								<Placeholder.Line />
							</Placeholder.Paragraph>
						</Placeholder>
					</Dimmer>
				</div>
			</Segment>
			<PluriannuelleFooter
				autorisationProgrammeCount={totaux?.nbProjetAp}
				autorisationEngagementCount={totaux?.nbProjetAe}
				montantTotalPeriode={totaux?.total}
				yearCount={years.length}
			/>
		</Segment>
	);
};

export default Pluriannuelle;
